import { useTheme } from "expo-router/react-navigation";
import { Gauge } from "lucide-react-native";
import { FC, useMemo } from "react";
import { View } from "react-native";

import { BarChart, useCopyableChart } from "@/components/charts";
import { Box, Card, HStack, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import SampleEditorialSummary from "./_SampleEditorialSummary";

/* ── Types ─────────────────────────────────────────────── */

export interface NPSReportData {
  /** Distinct respondents across the section, deduped on `persona.id`.
      Used for "(N respondents)" chart-title chips. */
  respondentCount: number;
  /** Counts indexed by score 0..10 (length always 11). Cookers
      that haven't observed a given score should still pass `0` so
      the histogram has a stable x-axis. */
  scoreCounts: number[];
}

interface NPSReportProps {
  data: NPSReportData;
  showSampleEditorialSummary?: boolean;
}

/* ── Sample fixture ─────────────────────────────────────── */

/* Synthetic 0–10 distribution.
   Detractors (0–6): 54  → 18%
   Passives  (7–8):  99  → 33%
   Promoters (9–10): 147 → 49%
   NPS = 49 − 18 = +31 (solid territory). */
export const SAMPLE_NPS_DATA: NPSReportData = {
  respondentCount: [6, 3, 6, 9, 9, 9, 12, 39, 60, 84, 63].reduce(
    (s, c) => s + c,
    0
  ),
  scoreCounts: [6, 3, 6, 9, 9, 9, 12, 39, 60, 84, 63],
};

/* ── Constants ──────────────────────────────────────────── */

/* NPS palette mirrors src/components/questions/results/NPSResults.tsx so the
   sample report reads in the same visual language as the live results page. */
const PROMOTER_COLOR = "#22c55e";
const PASSIVE_COLOR = "#f59e0b";
const DETRACTOR_COLOR = "#ef4444";

/* Same banding logic as NPSResults.tsx: ≥50 promoter-strong, ≥0 passive,
   <0 detractor. */
const scoreAccent = (score: number) =>
  score >= 50 ? PROMOTER_COLOR : score >= 0 ? PASSIVE_COLOR : DETRACTOR_COLOR;

/* ── Component ──────────────────────────────────────────── */

const NPSReport: FC<NPSReportProps> = ({
  data,
  showSampleEditorialSummary = false,
}) => {
  const { i18n } = useLocalization();
  const theme = useTheme();
  const primary = theme.colors.primary;

  const { chartRef, copyButton } = useCopyableChart();

  const fmtPct = (n: number) => i18n.numberToPercentage(n, { precision: 0 });
  const signedScore = (n: number) => (n > 0 ? `+${n}` : `${n}`);

  const counts = useMemo(() => data.scoreCounts ?? [], [data.scoreCounts]);
  const total = useMemo(
    () => counts.reduce((s, c) => s + (c ?? 0), 0),
    [counts]
  );

  const sumIn = (lo: number, hi: number) =>
    counts.slice(lo, hi + 1).reduce((s, c) => s + (c ?? 0), 0);

  const detractors = sumIn(0, 6);
  const passives = sumIn(7, 8);
  const promoters = sumIn(9, 10);

  /* No answers yet: render the shell, hand the chart an empty array (it shows
     its own "No data available"), and show "—" instead of a fabricated score —
     mirroring how `PricingResult` shows "—" rather than a fabricated price. */
  const hasData = total > 0;

  const npsScore =
    total > 0
      ? Math.round((promoters / total) * 100) -
        Math.round((detractors / total) * 100)
      : 0;
  const scoreColor = hasData ? scoreAccent(npsScore) : "#9ca3af";

  const histogramPoints = useMemo(
    () =>
      Array.from({ length: 11 }, (_, score) => ({
        x: String(score),
        y: counts[score] ?? 0,
      })),
    [counts]
  );

  /* Order: Detractors → Passives → Promoters (ascending score bands), so the
     segmented bar reads left-to-right from worst to best. */
  const segments = [
    {
      band: i18n.t("useCasePage.nps.sampleReport.segments.detractors.band"),
      color: DETRACTOR_COLOR,
      count: detractors,
      key: "detractors",
      label: i18n.t("dashboardNavigator.npsDetractors"),
    },
    {
      band: i18n.t("useCasePage.nps.sampleReport.segments.passives.band"),
      color: PASSIVE_COLOR,
      count: passives,
      key: "passives",
      label: i18n.t("dashboardNavigator.npsPassives"),
    },
    {
      band: i18n.t("useCasePage.nps.sampleReport.segments.promoters.band"),
      color: PROMOTER_COLOR,
      count: promoters,
      key: "promoters",
      label: i18n.t("dashboardNavigator.npsPromoters"),
    },
  ];

  return (
    <VStack className="gap-3">
      {showSampleEditorialSummary && (
        <SampleEditorialSummary
          body={i18n.t("useCasePage.nps.sampleReport.summary.body")}
          headline={i18n.t("useCasePage.nps.sampleReport.summary.headline")}
        />
      )}

      <Card className="p-6 rounded-2xl border border-outline-200">
        <VStack className="gap-3 lg:flex-row lg:items-center lg:gap-5">
          <VStack className="gap-2 lg:flex-1">
            <HStack className="items-center gap-4">
              <Box
                className="w-14 h-14 rounded-2xl items-center justify-center shrink-0"
                style={{ backgroundColor: primary }}
              >
                <Gauge color="white" size={26} strokeWidth={2.2} />
              </Box>
              <VStack className="flex-1 gap-1">
                <Text className="text-2xs font-semibold text-primary-600 uppercase tracking-wide">
                  {i18n.t("useCasePage.nps.sampleReport.score.badge")}
                </Text>
                <Text className="text-xl font-bold text-typography-900 tracking-tight">
                  {hasData
                    ? i18n.t(
                        "useCasePage.nps.sampleReport.score.classification"
                      )
                    : "—"}
                </Text>
              </VStack>
            </HStack>
            <Text className="text-xs text-typography-500 lg:pl-[72px]">
              {i18n.t("useCasePage.nps.sampleReport.score.subtitle")}
            </Text>
          </VStack>
          <VStack className="items-center lg:items-end gap-0">
            <Text
              className="text-4xl font-bold tracking-tight"
              style={{ color: scoreColor }}
            >
              {hasData ? signedScore(npsScore) : "—"}
            </Text>
            <Text className="text-2xs font-semibold text-typography-400 uppercase tracking-wide">
              {i18n.t("dashboardNavigator.npsScale")}
            </Text>
          </VStack>
        </VStack>

        <HStack
          className="w-full overflow-hidden rounded-lg mt-5"
          style={{ height: 12 }}
        >
          {segments.map((s) => {
            const pct = total > 0 ? (s.count / total) * 100 : 0;
            return pct > 0 ? (
              <Box
                key={s.key}
                style={{
                  backgroundColor: s.color,
                  width: `${pct}%`,
                }}
              />
            ) : null;
          })}
        </HStack>
      </Card>

      <HStack className="gap-3 flex-col sm:flex-row">
        {segments.map((s) => {
          const pct = total > 0 ? (s.count / total) * 100 : 0;
          return (
            <Card
              className="w-full sm:flex-1 p-5 rounded-2xl border border-outline-200 bg-background-50"
              key={s.key}
            >
              <VStack className="gap-1">
                <HStack className="items-center gap-2">
                  <Box
                    style={{
                      backgroundColor: s.color,
                      borderRadius: 3,
                      height: 10,
                      width: 10,
                    }}
                  />
                  <Text className="text-2xs font-semibold uppercase tracking-wide text-typography-500">
                    {s.band}
                  </Text>
                </HStack>
                <Text className="text-sm font-semibold text-typography-900 mt-1">
                  {s.label}
                </Text>
                <HStack className="items-baseline gap-2 mt-2">
                  <Text className="text-3xl font-bold tracking-tight text-typography-900">
                    {hasData ? fmtPct(pct) : "—"}
                  </Text>
                  <Text className="text-xs text-typography-500">
                    {i18n.t("useCasePage.nps.sampleReport.segments.count", {
                      count: s.count,
                    })}
                  </Text>
                </HStack>
              </VStack>
            </Card>
          );
        })}
      </HStack>

      <Card className="group p-6 rounded-2xl border border-outline-200">
        {copyButton}
        <VStack className="gap-5">
          <VStack className="gap-1 pb-2">
            <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
              {`${i18n.t("useCasePage.nps.sampleReport.chart.title")} (${i18n.t("dashboardNavigator.respondents", { count: data.respondentCount })})`}
            </Text>
            <Text className="text-xs text-typography-400">
              {i18n.t("useCasePage.nps.sampleReport.chart.subtitle")}
            </Text>
          </VStack>
          <View ref={chartRef} style={{ width: "100%" }}>
            <BarChart
              points={hasData ? histogramPoints : []}
              totalRespondents={total}
            />
          </View>
        </VStack>
      </Card>
    </VStack>
  );
};

export default NPSReport;
